import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';

import { take } from 'rxjs';

import { MonitoringDashboardSearchService } from '../../../Monitor/Core/services/monitoring-dashboard-search.service';
import { CurationAgentsService } from '../../services/curation-agents.service';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { applyAgentScope } from '../../models/agent-scope.util';

/**
 * Narrows the analytics dashboard to one agent.
 *
 * This has to happen before the dashboard is created, which is why it is a resolver rather than
 * something the section component does in ngOnInit. The dashboard re-fetches every series whenever
 * the filters change, and setting the criteria emits filters - so changing them once it is listening
 * feeds itself and the page never settles. Its own initialisation carries a cooldown for exactly
 * this reason; resolving first keeps the criteria in place before any of that starts.
 *
 * @TODO NEP-37332: the dashboard opens on All rather than the window shared with the other sections.
 * It reads its range from MonitoringDashboardSearchService.getDateQuery(), and an account search is
 * left on All by setDateQuery() - but calling setDateQuery() from here crashed the renderer, so the
 * cause needs profiling before it is wired up.
 */
@Injectable()
export class AgentAnalyticsFilterResolver implements Resolve<boolean> {

    constructor(
        private dashboardSearchService: MonitoringDashboardSearchService,
        private curationAgentsService: CurationAgentsService
    ) {}

    resolve(route: ActivatedRouteSnapshot): boolean {
        const agentId = Number(route.parent?.paramMap.get('agent_id'));

        if (!agentId) {
            return false;
        }

        // Agents act on saved coverage, so this is an account search narrowed to the one agent,
        // and within it to whatever the sidebar has ticked. AgentAnalyticsComponent keeps these in
        // step from here on.
        const criteria = new MentionSearchCriteriaModel();
        criteria.isAccountSearch = true;

        this.curationAgentsService.agentScope$.pipe(take(1)).subscribe(agentScope =>
            applyAgentScope(criteria.query, agentId, agentScope));

        // The shared window is carried on the criteria. Setting the search service's own date query
        // from here crashed the renderer - see the @TODO below - so the dashboard is left to take
        // its range the way it does for a folder.
        this.curationAgentsService.selectedDateRange$.pipe(take(1)).subscribe(dateRange => {
            if (dateRange) {
                criteria.query.dateQuery = dateRange;
            }
        });

        this.dashboardSearchService.setSearchCriteria(criteria);

        return true;
    }
}
